/**
 * Manage Board — parent/admin-only area that replaces inline tile edit
 * bubbles. NOT reachable from a paired child device because:
 *   1. The route is not in `ChildDeviceShell`'s allow-list (silently
 *      redirected away to the child's default board).
 *   2. The Edit pill on Board.jsx (which links here) is itself hidden on
 *      paired devices.
 *
 * Six tabs, per the product spec:
 *   1. Create a Button         (rich — grid + click-to-edit + new + dup + del)
 *   2. Colors & Background     (profile avatar + tile color palette)
 *   3. Day / Night / School    (start/end times + overrides)
 *   4. Parental Settings       (caregiver PIN, kid-mode default, consent link)
 *   5. Therapist Settings      (gated — only renders if user has therapist role)
 *   6. School Settings         (school contact + privacy toggles)
 *
 * No "AI generate / camera / voice record / share / export" controls are
 * added here. Tab 1 reuses the existing `TileEditor` component which
 * already feature-gates those internally — we don't widen surface area.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Copy as CopyIcon,
  Trash2,
  Paintbrush,
  Sun,
  Shield,
  ShieldCheck,
  GraduationCap,
  School as SchoolIcon,
  Pencil,
  Sparkles,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import EmojiPicker from "@/components/EmojiPicker";
import TileEditor from "@/components/TileEditor";
import AddTileWizard from "@/components/AddTileWizard";
import EncryptedChildInfoCard from "@/components/EncryptedChildInfoCard";
import { setLocalImage, setLocalAudio, hydrateTilesWithLocalMedia } from "@/lib/localMedia";
import BoardLevelCard from "@/components/BoardLevelCard";

const COLOR_OPTIONS = [
  "#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#C39BD3",
  "#FFA07A", "#20B2AA", "#FF69B4",
];

const TABS = [
  { id: "create-button", label: "Create a Button", icon: Pencil },
  { id: "colors", label: "Colors & Background", icon: Paintbrush },
  { id: "day-night-school", label: "Day / Night / School Mode", icon: Sun },
  { id: "permissions", label: "Manage Board (Permissions)", icon: Shield },
  { id: "parental", label: "Parental Settings", icon: ShieldCheck },
  { id: "therapist", label: "Therapist Settings", icon: GraduationCap },
  { id: "school", label: "School Settings", icon: SchoolIcon },
];

export default function ManageBoard() {
  const { profileId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "create-button";
  const setTab = (id) => setParams({ tab: id }, { replace: true });

  useSEO({
    title: "Manage board",
    description: "Edit your child's AAC board from any device.",
    path: `/manage/${profileId}`,
  });

  const [profile, setProfile] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, ts, cs] = await Promise.all([
        api.get(`/profiles/${profileId}`),
        api.get(`/profiles/${profileId}/tiles`),
        api.get(`/profiles/${profileId}/categories`),
      ]);
      setProfile(p.data);
      const hydrated = await hydrateTilesWithLocalMedia(profileId, ts.data || []);
      setTiles(hydrated);
      setCategories(cs.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      if (e.response?.status === 404) nav("/profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to={`/board/${profileId}`}
            className="pill-btn"
            data-testid="manage-board-back"
          >
            <ArrowLeft size={16} />
          </Link>
          <h1
            className="display text-2xl sm:text-3xl font-extrabold"
            data-testid="manage-board-title"
          >
            Manage board {profile?.name && <span className="text-[color:var(--ink)]/60">— {profile.name}</span>}
          </h1>
        </div>
        <p className="text-sm text-[color:var(--ink)]/70 mb-5">
          Edit buttons, colors, and modes here from any device. The child's
          tablet stays in communication-only mode.
        </p>

        <div
          className="brutal-card bg-[color:var(--cream)] p-3 text-xs sm:text-sm mb-5 flex items-start gap-2"
          data-testid="manage-board-privacy-banner"
        >
          <span aria-hidden className="text-base shrink-0">🔒</span>
          <span>
            <strong>Your child's privacy.</strong> Photos, voice, button-press
            history and other child data stay <strong>only on this device</strong>.
            The cloud stores adult account info + your board's structure
            (words, colors, categories) so you can restore it on a new device —
            but never your child's photos or voice.{" "}
            <Link to="/privacy-promise" className="underline font-bold" data-testid="manage-board-privacy-promise-link">
              Read our full privacy promise →
            </Link>
          </span>
        </div>

        {error && (
          <div
            className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-4"
            data-testid="manage-board-error"
          >
            {error}
          </div>
        )}

        <nav
          className="flex flex-wrap gap-2 mb-6"
          data-testid="manage-board-tabs"
          role="tablist"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`pill-btn ${active ? "gold" : ""}`}
                aria-current={active ? "page" : undefined}
                aria-controls={`manage-board-panel-${t.id}`}
                role="tab"
                data-testid={`manage-board-tab-${t.id}`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>

        <div id={`manage-board-panel-${tab}`} role="tabpanel">
          {loading && (
            <div className="brutal-card bg-white p-6 text-sm text-[color:var(--ink)]/60">
              Loading…
            </div>
          )}
          {!loading && tab === "create-button" && (
            <CreateButtonTab
              profile={profile}
              profileId={profileId}
              tiles={tiles}
              categories={categories}
              onChange={load}
            />
          )}
          {!loading && tab === "colors" && profile && (
            <ColorsTab profile={profile} onSaved={load} />
          )}
          {!loading && tab === "day-night-school" && profile && (
            <DayNightSchoolTab profile={profile} onSaved={load} />
          )}
          {!loading && tab === "parental" && profile && (
            <ParentalTab profileId={profileId} profile={profile} onSaved={load} />
          )}
          {!loading && tab === "permissions" && profile && (
            <PermissionsTab profile={profile} />
          )}
          {!loading && tab === "therapist" && <TherapistTab />}
          {!loading && tab === "school" && profile && (
            <SchoolTab profile={profile} onSaved={load} />
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- TAB 1 ------
 * Create a Button — the rich tab. Reuses the existing TileEditor so AI /
 * camera / voice gating stays in one place. Adds:
 *   - Click an existing tile to edit
 *   - "Create new" → opens TileEditor with `initial: null`
 *   - Duplicate (POST with copied fields, no media to avoid quota surprises)
 *   - Delete with type-to-confirm
 * -------------------------------------------------------------------------- */
function CreateButtonTab({ profile, profileId, tiles, categories, onChange }) {
  const [editor, setEditor] = useState({ open: false, initial: null });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");

  const visible = useMemo(() => {
    if (filterCat === "__all__") return tiles;
    return tiles.filter((t) => t.category_id === filterCat);
  }, [tiles, filterCat]);

  const handleSave = async (data) => {
    setBusy(true);
    setError("");
    try {
      // Phase 3 / local-first: media stays on device.
      const { custom_image_b64, custom_audio_b64, ...cloudBody } = data;
      if (editor.initial?.id) {
        const tileId = editor.initial.id;
        if (custom_image_b64 !== undefined) await setLocalImage(profileId, tileId, custom_image_b64 || null);
        if (custom_audio_b64 !== undefined) await setLocalAudio(profileId, tileId, custom_audio_b64 || null);
        await api.put(`/tiles/${tileId}`, cloudBody);
      } else {
        const { data: created } = await api.post(`/profiles/${profileId}/tiles`, cloudBody);
        if (custom_image_b64) await setLocalImage(profileId, created.id, custom_image_b64);
        if (custom_audio_b64) await setLocalAudio(profileId, created.id, custom_audio_b64);
      }
      setEditor({ open: false, initial: null });
      onChange();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save tile.");
    } finally {
      setBusy(false);
    }
  };

  // Wizard variant — always a CREATE (no editor.initial path). Throws on
  // failure so the wizard can keep itself open and show the error inline.
  const handleWizardSave = async (data) => {
    setError("");
    try {
      const { custom_image_b64, custom_audio_b64, ...cloudBody } = data;
      const { data: created } = await api.post(`/profiles/${profileId}/tiles`, cloudBody);
      if (custom_image_b64) await setLocalImage(profileId, created.id, custom_image_b64);
      if (custom_audio_b64) await setLocalAudio(profileId, created.id, custom_audio_b64);
      onChange();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not save tile.";
      setError(msg);
      throw new Error(msg);
    }
  };

  const handleDuplicate = async (tile) => {
    setBusy(true);
    setError("");
    try {
      // Duplicate the editable fields only — strip media to avoid quota
      // surprises and the heavy lift of re-encoding base64 client-side.
      await api.post(`/profiles/${profileId}/tiles`, {
        label: `${tile.label} (copy)`,
        speak_text: tile.speak_text || null,
        emoji: tile.emoji || "🔤",
        image_url: tile.image_url || null,
        color: tile.color,
        category_id: tile.category_id,
        is_favorite: false,
        is_gestalt: !!tile.is_gestalt,
        time_scope: tile.time_scope || "anytime",
        order: (tile.order || 0) + 1,
      });
      onChange();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not duplicate tile.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (tile) => {
    if (!window.confirm(`Delete "${tile.label}"? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/tiles/${tile.id}`);
      onChange();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete tile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-create-button"
    >
      {profile && (
        <BoardLevelCard
          profile={profile}
          tileCount={tiles.length}
          onChanged={onChange}
        />
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-lg font-extrabold flex-1">Buttons</h2>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="brutal-input"
          data-testid="manage-board-tile-filter"
        >
          <option value="__all__">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="pill-btn primary"
          disabled={busy}
          data-testid="manage-board-tile-new"
        >
          <Plus size={16} /> Create new
        </button>
      </div>

      {error && (
        <div
          className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-3"
          data-testid="manage-board-tile-error"
        >
          {error}
        </div>
      )}

      {visible.length === 0 ? (
        <div
          className="text-sm text-[color:var(--ink)]/60 py-8 text-center"
          data-testid="manage-board-tile-empty"
        >
          No buttons yet. Tap <strong>Create new</strong> to add the first one.
        </div>
      ) : (
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="manage-board-tile-list"
        >
          {visible.map((tile) => (
            <li
              key={tile.id}
              className="brutal-card p-3 bg-[color:var(--cream)] flex flex-col"
              data-testid={`manage-tile-${tile.id}`}
            >
              <button
                type="button"
                onClick={() => setEditor({ open: true, initial: tile })}
                className="text-left flex-1"
                aria-label={`Edit ${tile.label}`}
                data-testid={`manage-tile-edit-${tile.id}`}
              >
                <div
                  className="w-full aspect-square rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-4xl mb-2"
                  style={{ background: tile.color }}
                >
                  <span aria-hidden>{tile.emoji || "🔤"}</span>
                </div>
                <div className="font-bold truncate">{tile.label}</div>
                <div className="text-xs text-[color:var(--ink)]/60 truncate">
                  {categories.find((c) => c.id === tile.category_id)?.label || "Uncategorised"}
                </div>
              </button>
              <div className="flex gap-1 mt-2">
                <button
                  type="button"
                  onClick={() => handleDuplicate(tile)}
                  disabled={busy}
                  className="pill-btn flex-1"
                  aria-label={`Duplicate ${tile.label}`}
                  data-testid={`manage-tile-duplicate-${tile.id}`}
                >
                  <CopyIcon size={12} />
                  <span className="hidden sm:inline">Duplicate</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(tile)}
                  disabled={busy}
                  className="pill-btn"
                  aria-label={`Delete ${tile.label}`}
                  data-testid={`manage-tile-delete-${tile.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <TileEditor
        open={editor.open}
        initial={editor.initial}
        categories={categories}
        onClose={() => setEditor({ open: false, initial: null })}
        onSave={handleSave}
        profileId={profileId}
      />

      <AddTileWizard
        open={wizardOpen}
        categories={categories}
        onClose={() => setWizardOpen(false)}
        onSave={handleWizardSave}
        lang={profile?.language || "en"}
        profileId={profileId}
        aiEnabled={!!profile?.ai_suggestions}
      />
    </section>
  );
}

/* -------------------------------------------------------------- TAB 2 ------
 * Colors & Background — profile-level avatar + accent color. Lifted from
 * Settings.jsx → only the controls relevant to the BOARD's visual identity.
 * -------------------------------------------------------------------------- */
function ColorsTab({ profile, onSaved }) {
  const [avatar, setAvatar] = useState(profile.avatar || "");
  const [color, setColor] = useState(profile.color || COLOR_OPTIONS[0]);
  const [busy, setBusy] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/profiles/${profile.id}`, { ...profile, avatar, color });
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 1800);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-colors"
    >
      <h2 className="text-lg font-extrabold mb-3">Profile look</h2>
      <div className="mb-4">
        <div className="font-semibold mb-1">Avatar</div>
        <EmojiPicker value={avatar} onChange={setAvatar} />
      </div>
      <div className="mb-4">
        <div className="font-semibold mb-2">Accent color</div>
        <div className="flex flex-wrap gap-2">
          {COLOR_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`w-10 h-10 rounded-full border-[3px] border-[color:var(--ink)] ${color === c ? "ring-4 ring-[color:var(--gold)]" : ""}`}
              style={{ background: c }}
              aria-label={`Color ${c}`}
              data-testid={`manage-color-${c}`}
            />
          ))}
        </div>
      </div>
      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="manage-board-colors-error">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        {savedFlag && <span className="text-emerald-700 text-sm font-bold">Saved ✓</span>}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="pill-btn primary"
          data-testid="manage-board-colors-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- TAB 3 ------
 * Day / Night / School Mode — the four time fields + the overrides.
 * -------------------------------------------------------------------------- */
function DayNightSchoolTab({ profile, onSaved }) {
  const [dayStart, setDayStart] = useState(profile.day_start || "07:00");
  const [nightStart, setNightStart] = useState(profile.night_start || "20:00");
  const [schoolStart, setSchoolStart] = useState(profile.school_start || "08:00");
  const [schoolEnd, setSchoolEnd] = useState(profile.school_end || "15:00");
  const [modeOverride, setModeOverride] = useState(profile.mode_override || "auto");
  const [schoolOverride, setSchoolOverride] = useState(profile.school_mode_override || "auto");
  const [busy, setBusy] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/profiles/${profile.id}`, {
        ...profile,
        day_start: dayStart,
        night_start: nightStart,
        school_start: schoolStart,
        school_end: schoolEnd,
        mode_override: modeOverride,
        school_mode_override: schoolOverride,
      });
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 1800);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-day-night-school"
    >
      <h2 className="text-lg font-extrabold mb-3">Day, night & school</h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <label className="text-sm">
          <span className="block mb-1 font-bold">Day starts</span>
          <input
            type="time"
            value={dayStart}
            onChange={(e) => setDayStart(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-day-start"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1 font-bold">Night starts</span>
          <input
            type="time"
            value={nightStart}
            onChange={(e) => setNightStart(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-night-start"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1 font-bold">School starts</span>
          <input
            type="time"
            value={schoolStart}
            onChange={(e) => setSchoolStart(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-school-start"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1 font-bold">School ends</span>
          <input
            type="time"
            value={schoolEnd}
            onChange={(e) => setSchoolEnd(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-school-end"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1 font-bold">Day/night override</span>
          <select
            value={modeOverride}
            onChange={(e) => setModeOverride(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-mode-override"
          >
            <option value="auto">Auto (by clock)</option>
            <option value="day">Always day</option>
            <option value="night">Always night</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1 font-bold">School override</span>
          <select
            value={schoolOverride}
            onChange={(e) => setSchoolOverride(e.target.value)}
            className="brutal-input w-full"
            data-testid="manage-board-school-override"
          >
            <option value="auto">Auto (weekday hours)</option>
            <option value="home">Always home</option>
            <option value="school">Always school</option>
          </select>
        </label>
      </div>
      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="manage-board-dns-error">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        {savedFlag && <span className="text-emerald-700 text-sm font-bold">Saved ✓</span>}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="pill-btn primary"
          data-testid="manage-board-dns-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- TAB 4 ------
 * Parental Settings — caregiver PIN, kid-mode default, link to /privacy/me.
 * The PIN is hashed server-side; this UI only sets a fresh value.
 * -------------------------------------------------------------------------- */
function ParentalTab({ profileId, profile, onSaved }) {
  const [pin, setPin] = useState("");
  const [kidMode, setKidMode] = useState(profile.kid_mode !== false);
  const [busy, setBusy] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [error, setError] = useState("");
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState("");

  useEffect(() => {
    setLogLoading(true);
    api.get(`/profiles/${profileId}/access-log`)
      .then((r) => setLogItems(r.data?.items || []))
      .catch((e) => setLogError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load access log."))
      .finally(() => setLogLoading(false));
  }, [profileId]);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { ...profile, kid_mode: kidMode };
      if (pin.trim()) body.caregiver_pin = pin.trim();
      await api.put(`/profiles/${profile.id}`, body);
      setSavedFlag(true);
      setPin("");
      setTimeout(() => setSavedFlag(false), 1800);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-parental"
    >
      <h2 className="text-lg font-extrabold mb-3">Parental settings</h2>
      <EncryptedChildInfoCard profile={profile} onSaved={onSaved} />
      <label className="block mb-4">
        <span className="block mb-1 font-bold text-sm">Caregiver PIN</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          value={pin}
          placeholder={profile.caregiver_pin ? "•••• (currently set)" : "Set a 4–8 digit PIN"}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          className="brutal-input w-48"
          data-testid="manage-board-pin-input"
        />
        <p className="text-xs text-[color:var(--ink)]/60 mt-1">
          A PIN protects the Caregiver toggle on devices that aren't paired
          as child tablets. Leave blank to keep the current PIN.
        </p>
      </label>
      <label className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          checked={kidMode}
          onChange={(e) => setKidMode(e.target.checked)}
          className="w-5 h-5 accent-[color:var(--coral)]"
          data-testid="manage-board-kid-mode-default"
        />
        <span className="text-sm">
          Default to kid mode (clean board, no chrome) when the board opens.
        </span>
      </label>
      <SchoolConsentToggle profile={profile} onSaved={onSaved} />

      <div className="mb-4 text-sm">
        <Link to="/privacy/me" className="underline" data-testid="manage-board-privacy-link">
          View privacy & consent status →
        </Link>
      </div>
      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="manage-board-parental-error">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        {savedFlag && <span className="text-emerald-700 text-sm font-bold">Saved ✓</span>}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="pill-btn primary"
          data-testid="manage-board-parental-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Access log — who has touched this child's account */}
      <div className="mt-6 pt-5 border-t-[3px] border-[color:var(--ink)]">
        <h3 className="font-extrabold text-base mb-2">Account access</h3>
        <p className="text-xs text-[color:var(--ink)]/70 mb-3">
          A running list of who has accessed this child's profile and what was
          changed. Includes logins, tile edits, photo uploads, exports, and
          tablet pairings.
        </p>
        {logLoading && <div className="text-sm text-[color:var(--ink)]/60">Loading…</div>}
        {logError && (
          <div className="text-sm text-red-600" data-testid="manage-board-parental-log-error">
            {logError}
          </div>
        )}
        {!logLoading && !logError && logItems.length === 0 && (
          <div
            className="text-sm text-[color:var(--ink)]/60 py-4 text-center"
            data-testid="manage-board-parental-log-empty"
          >
            Nothing yet — every change made to this board will show up here.
          </div>
        )}
        {!logLoading && logItems.length > 0 && (
          <ul className="divide-y text-sm" data-testid="manage-board-parental-log-list">
            {logItems.map((ev, i) => (
              <li
                key={`${ev.action}-${ev.ts || i}`}
                className="py-2 flex items-start gap-3"
                data-testid={`manage-board-parental-log-row-${i}`}
              >
                <span className="text-xs text-[color:var(--ink)]/60 shrink-0 w-32">
                  {ev.ts ? new Date(ev.ts).toLocaleString() : ""}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{prettyAction(ev.action)}</div>
                  <div className="text-xs text-[color:var(--ink)]/60 truncate">
                    {ev.actor_email || "—"}
                    {ev.ip ? ` · ${ev.ip}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Danger zone — board deletion (Defect-batch-2 #4, 2026-07-04).
          Destructive actions live ONLY here, never in normal flows, and
          are gated behind type-to-confirm. */}
      <DangerZone profile={profile} />
    </section>
  );
}

/* -------------------------------------------------------------- DANGER ZONE
 * Type-to-confirm board deletion. This is the ONLY place in the entire
 * app where a caregiver can delete a board. Nothing destructive is
 * reachable outside Parental Controls per the standing rule.
 * -------------------------------------------------------------------------- */
function DangerZone({ profile }) {
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const boardName = `${profile.name}'s board`;
  const typedOk = confirmText.trim() === boardName;

  const doDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/profiles/${profile.id}`);
      // Full navigation away — resetting all React state so the *next*
      // delete a caregiver performs starts from a clean board list.
      // Fixes defect-batch-2 #2 ("delete works once, second delete
      // silently fails") which was a stale-list problem when the delete
      // stayed on the same page and just filtered React state.
      nav("/pick-board", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete this board.");
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t-[3px] border-[color:var(--coral)]" data-testid="manage-board-danger-zone">
      <h3 className="font-extrabold text-base mb-1 text-[color:var(--coral-deep)]">
        Danger zone
      </h3>
      <p className="text-xs text-[color:var(--ink)]/70 mb-3">
        Permanent actions live here. Nothing destructive is reachable
        anywhere else in the app.
      </p>
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="pill-btn danger"
          data-testid="danger-zone-open-delete"
        >
          Delete this board…
        </button>
      ) : (
        <div className="brutal-card bg-[#FFF3F0] p-4">
          <div className="text-sm font-bold mb-1">Permanently delete <em>{boardName}</em>?</div>
          <p className="text-xs text-[color:var(--ink)]/70 mb-3">
            All tiles, categories, and audit history for this board will be removed.
            Photos and voice recordings stored on this device remain in your local
            storage — clear them from <Link to="/reset-sw.html" className="underline">Reset app cache</Link> if you want to fully wipe.
          </p>
          <label className="block text-xs mb-2">
            Type <strong>{boardName}</strong> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="brutal-input w-full mb-3"
            placeholder={boardName}
            autoComplete="off"
            data-testid="danger-zone-confirm-input"
          />
          {error && (
            <div className="text-sm text-red-600 mb-2" data-testid="danger-zone-error">{error}</div>
          )}
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setExpanded(false); setConfirmText(""); setError(""); }}
              className="pill-btn"
              disabled={busy}
              data-testid="danger-zone-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="pill-btn danger"
              disabled={busy || !typedOk}
              data-testid="danger-zone-delete-confirm"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Translate raw audit action strings into the friendlier copy parents see. */
function prettyAction(action) {
  if (!action) return "Activity";
  const MAP = {
    "profile.create": "Created this board",
    "profile.update": "Updated profile settings",
    "tile.create": "Added a tile",
    "tile.update": "Edited a tile",
    "tile.delete": "Deleted a tile",
    "tile.create_with_image": "Added a tile with a photo",
    "child_device.pair_initiated": "Generated a tablet pairing code",
    "child_device.pair_claimed": "Paired a child tablet",
    "child_device.unpaired": "Unpaired a child tablet",
    "profile.board_level.add_recommended": "Added recommended tiles for level",
    "auth.login": "Signed in",
    "billing.doctor_code_redeemed": "Redeemed a sponsor / doctor code",
    "feedback.coming_soon_submitted": "Submitted feedback",
  };
  return MAP[action] || action.replace(/\./g, " ").replace(/_/g, " ");
}

/* -------------------------------------------------------------- TAB 5 ------
 * Permissions — summary of who currently has access to this child's
 * board (school staff invites + therapist invites + paired tablets).
 * Stays read-only for now; granting/revoking happens through the existing
 * /admin/child-devices flow + future therapist/school invite flows. The
 * intent is to give parents one screen to answer "who can see this?".
 * -------------------------------------------------------------------------- */
function PermissionsTab({ profile }) {
  const [tablets, setTablets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/child-device/list")
      .then((r) => {
        const all = r.data?.devices || [];
        const mine = all.filter(
          (d) => !d.default_profile_id || d.default_profile_id === profile.id
        );
        setTablets(mine);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profile.id]);

  return (
    <section className="brutal-card bg-white p-5" data-testid="manage-board-permissions">
      <h2 className="text-lg font-extrabold mb-3">Manage Board — Permissions</h2>
      <p className="text-sm text-[color:var(--ink)]/70 mb-4">
        A summary of who currently has access to <strong>{profile.name}'s</strong>{" "}
        board. Grant or revoke access from the buttons below.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="brutal-card bg-[color:var(--cream)] p-3">
          <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">
            Paired child tablets
          </div>
          {loading ? (
            <div className="text-sm">Loading…</div>
          ) : tablets.length === 0 ? (
            <div className="text-sm text-[color:var(--ink)]/60">None paired</div>
          ) : (
            <ul className="text-sm" data-testid="manage-board-permissions-tablets">
              {tablets.map((t) => (
                <li key={t.id} className="truncate">{t.label}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="brutal-card bg-[color:var(--cream)] p-3">
          <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">
            Therapists with access
          </div>
          <div className="text-sm text-[color:var(--ink)]/60">
            None — therapist invites open soon.
          </div>
        </div>
        <div className="brutal-card bg-[color:var(--cream)] p-3">
          <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">
            School staff with access
          </div>
          <div className="text-sm text-[color:var(--ink)]/60">
            {profile.allow_staff_access_requests === false
              ? "Staff requests are turned OFF."
              : "Staff requests are allowed, but no one has been granted yet."}
          </div>
        </div>
        <div className="brutal-card bg-[color:var(--cream)] p-3">
          <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">
            Quick actions
          </div>
          <div className="flex flex-col gap-2 mt-1">
            <Link
              to="/admin/child-devices"
              className="pill-btn"
              data-testid="manage-board-permissions-tablets-link"
            >
              Manage paired tablets →
            </Link>
            <Link to="/therapist-portal" className="pill-btn" data-testid="manage-board-permissions-therapist-link">
              Therapist invites (soon)
            </Link>
            <Link to="/schools-portal" className="pill-btn" data-testid="manage-board-permissions-schools-link">
              School access (soon)
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- TAB 5 ------
 * Therapist Settings — only renders the dashboard link if the user has
 * the therapist role. Otherwise shows the "ask a clinician to apply" copy.
 * No client-edit controls live here — those are at /clients (existing
 * therapist dashboard). Keeps Manage Board free of cross-account writes.
 * -------------------------------------------------------------------------- */
function TherapistTab() {
  const { user } = useAuth();
  const isTherapist = (user?.roles || []).includes("therapist");
  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-therapist"
    >
      <h2 className="text-lg font-extrabold mb-3">Therapist settings</h2>
      {isTherapist ? (
        <>
          <p className="text-sm text-[color:var(--ink)]/70 mb-3">
            You have therapist access. Client boards are managed from your
            dashboard.
          </p>
          <Link
            to="/clients"
            className="pill-btn primary"
            data-testid="manage-board-therapist-dashboard"
          >
            <Sparkles size={14} /> Open therapist dashboard
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-[color:var(--ink)]/70 mb-3">
            If you're a licensed SLP/OT/BCBA, you can apply for therapist
            access to sponsor families and manage multiple boards from one
            dashboard.
          </p>
          <Link
            to="/therapist-application"
            className="pill-btn"
            data-testid="manage-board-therapist-apply"
          >
            Apply for therapist access →
          </Link>
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- TAB 6 ------
 * School Settings — school-context fields stored on the profile. Today
 * limited to a free-text "school contact" + a privacy toggle for whether
 * teachers/staff can request access. Sensitive future features (live
 * teacher edit, classroom analytics) stay hidden behind feature flags
 * elsewhere; we don't add new write surfaces here.
 * -------------------------------------------------------------------------- */
function SchoolTab({ profile, onSaved }) {
  const [contact, setContact] = useState(profile.school_contact || "");
  const [allowStaffRequests, setAllowStaffRequests] = useState(
    profile.allow_staff_access_requests !== false
  );
  const [busy, setBusy] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/profiles/${profile.id}`, {
        ...profile,
        school_contact: contact.trim() || null,
        allow_staff_access_requests: allowStaffRequests,
      });
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 1800);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid="manage-board-school"
    >
      <h2 className="text-lg font-extrabold mb-3">School settings</h2>
      <label className="block mb-4">
        <span className="block mb-1 font-bold text-sm">
          School / classroom contact
        </span>
        <input
          type="text"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          maxLength={200}
          placeholder="e.g. Ms. Carter, Room 4 — 555-0100"
          className="brutal-input w-full"
          data-testid="manage-board-school-contact"
        />
        <p className="text-xs text-[color:var(--ink)]/60 mt-1">
          Free-text only — saved locally to your account. We never share this
          with anyone outside your family.
        </p>
      </label>
      <label className="flex items-start gap-2 mb-4">
        <input
          type="checkbox"
          checked={allowStaffRequests}
          onChange={(e) => setAllowStaffRequests(e.target.checked)}
          className="w-5 h-5 accent-[color:var(--coral)] mt-0.5"
          data-testid="manage-board-allow-staff"
        />
        <span className="text-sm">
          Allow school staff to <strong>request</strong> read-only access to
          this board. You'll be emailed and have to approve every request —
          staff cannot edit or download anything.
        </span>
      </label>
      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="manage-board-school-error">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        {savedFlag && <span className="text-emerald-700 text-sm font-bold">Saved ✓</span>}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="pill-btn primary"
          data-testid="manage-board-school-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// SchoolConsentToggle — one-time parent consent for school auto-launch.
// When ON, tile changes proposed by a `school_official` helper are applied
// to the child's runtime immediately and surfaced to the parent as a real-
// time `auto_launched` notification rather than a blocking approval.
// Off by default; the parent can revoke at any time.
// ---------------------------------------------------------------------------
function SchoolConsentToggle({ profile, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isConsented = !!profile.school_auto_apply_consent_at;

  const flip = async (consent) => {
    setBusy(true);
    setError("");
    try {
      const next = consent ? new Date().toISOString() : null;
      await api.put(`/profiles/${profile.id}`, {
        ...profile,
        school_auto_apply_consent_at: next,
      });
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-white p-4 mb-4"
      data-testid="manage-board-school-consent"
    >
      <h3 className="font-extrabold text-base mb-2">School mode — auto-apply</h3>
      <p className="text-xs text-[color:var(--ink-soft)] mb-2 leading-snug">
        When ON, changes proposed by a <strong>School</strong> helper apply to
        your child's runtime <em>immediately</em>. You still see a real-time
        notification with a full acceptance report. When OFF (default),
        school changes wait for your Approve & Apply tap.
      </p>
      {isConsented ? (
        <div className="brutal-card bg-[color:var(--cream)] p-3 text-sm mb-2">
          <strong>Auto-apply ON</strong> since{" "}
          {new Date(profile.school_auto_apply_consent_at).toLocaleDateString()}.
          School helpers can edit your child's library without your tap.
        </div>
      ) : (
        <div className="brutal-card bg-[color:var(--paper-muted)] p-3 text-sm mb-2">
          <strong>Auto-apply OFF.</strong> Every school change waits for your
          one-tap approval (recommended for most families).
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="school-consent-error">{error}</div>
      )}
      <div className="flex justify-end">
        {isConsented ? (
          <button
            type="button"
            onClick={() => flip(false)}
            disabled={busy}
            className="pill-btn"
            data-testid="school-consent-revoke"
          >
            {busy ? "Saving…" : "Revoke consent"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => flip(true)}
            disabled={busy}
            className="pill-btn primary"
            data-testid="school-consent-grant"
          >
            {busy ? "Saving…" : "Grant one-time consent"}
          </button>
        )}
      </div>
    </section>
  );
}
